import { motion } from "framer-motion";
import { Play, ExternalLink, Tv } from "lucide-react";
import { SITE } from "../../lib/site";

const TV_VIDEO_ID = "jcc0lntg5PI";

const TILES = [
  {
    name: "TikTok",
    handle: "@coulisse.dj",
    desc: "+258k abonnés · Coulisses & best-of",
    url: SITE.socials.tiktok,
    image:
      "https://images.unsplash.com/photo-1571266028243-d220bc5e6b73?auto=format&fit=crop&w=1200&q=70",
    accent: "from-[#FF2D9C]/40 to-transparent",
  },
  {
    name: "Instagram",
    handle: "@dj_as_adriensebert",
    desc: "+16k abonnés · Mariages & événements",
    url: SITE.socials.instagram,
    image:
      "https://images.unsplash.com/photo-1505236858219-8359eb29e329?auto=format&fit=crop&w=1200&q=70",
    accent: "from-[#A855F7]/40 to-transparent",
  },
  {
    name: "YouTube",
    handle: "Coulisse DJ - Adrien Sebert",
    desc: "Vidéos de prestations & matériel",
    url: SITE.socials.youtube,
    image:
      "https://images.unsplash.com/photo-1493676304819-0d7a8d026dcf?auto=format&fit=crop&w=1200&q=70",
    accent: "from-red-500/40 to-transparent",
  },
  {
    name: "Facebook",
    handle: "Adrien Sebert (DJ AS)",
    desc: "+60k abonnés · Communauté locale",
    url: SITE.socials.facebook,
    image:
      "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=70",
    accent: "from-[#1877F2]/40 to-transparent",
  },
];

export const Universe = () => {
  return (
    <section
      id="universe"
      data-testid="universe-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="eyebrow">Découvrez mon univers</span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Avant de réserver, <span className="gold-text-gradient">venez voir l'envers du décor.</span>
            </h2>
            <p className="mt-4 text-zinc-400">
              Mes prestations, mon matériel, ma vision du DJing — tout est partagé,
              en transparence, sur mes réseaux.
            </p>
          </div>
        </div>

        {/* Featured: TV interview */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.7 }}
          data-testid="tv-interview"
          className="mb-6 grid lg:grid-cols-12 gap-5 items-stretch"
        >
          <div className="lg:col-span-8 relative rounded-2xl overflow-hidden border border-[#D4AF37]/30 gold-glow bg-black">
            <div className="aspect-video w-full">
              <iframe
                src={`https://www.youtube.com/embed/${TV_VIDEO_ID}?rel=0&modestbranding=1`}
                title="Interview TV – Adrien Sebert / DJ AS Animation"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute inset-0 w-full h-full"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-4 glass rounded-2xl p-7 md:p-8 flex flex-col justify-center">
            <div className="inline-flex items-center gap-2 self-start px-3 py-1.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-[11px] tracking-[0.28em] uppercase">
              <Tv size={13} /> Vu à la télévision
            </div>
            <h3 className="mt-5 text-2xl md:text-3xl font-bold text-white tracking-tight">
              Mon <span className="gold-text-gradient">interview TV</span>
            </h3>
            <p className="mt-4 text-zinc-400 leading-relaxed text-sm md:text-[15px]">
              Une rencontre où je partage mon parcours, ma vision du métier de DJ
              professionnel et ce qui me différencie depuis plus de 20 ans.
            </p>
            <a
              href={`https://youtu.be/${TV_VIDEO_ID}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="tv-interview-watch-btn"
              className="btn-outline-gold mt-6 self-start rounded-full px-5 py-3 text-sm inline-flex items-center gap-2"
            >
              Regarder sur YouTube <ExternalLink size={14} />
            </a>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {TILES.map((t, i) => (
            <motion.a
              key={t.name}
              href={t.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              data-testid={`universe-${t.name.toLowerCase()}`}
              className="group relative overflow-hidden rounded-2xl aspect-[16/9] border border-white/10 hover:border-[#D4AF37]/40 transition"
            >
              <img
                src={t.image}
                alt={t.name}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-105 group-hover:opacity-75 transition-all duration-700"
              />
              <div className={`absolute inset-0 bg-gradient-to-tr ${t.accent}`} />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
              <div className="relative z-10 h-full flex items-end p-6 md:p-8">
                <div className="flex-1">
                  <div className="text-xs tracking-[0.28em] uppercase text-[#D4AF37]">
                    {t.name}
                  </div>
                  <div className="mt-2 text-lg md:text-xl font-semibold text-white">
                    {t.handle}
                  </div>
                  <div className="mt-1 text-sm text-zinc-300">{t.desc}</div>
                </div>
                <div className="w-12 h-12 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white border border-white/20 group-hover:bg-[#D4AF37] group-hover:text-black transition-colors">
                  <ExternalLink size={18} />
                </div>
              </div>
              <div className="absolute top-5 left-5 w-12 h-12 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-white">
                <Play size={18} className="translate-x-0.5" />
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
};
